import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { datadogRum } from '@datadog/browser-rum';

import type { ConnectStatementsInstitution } from './useConnectStatementsData';

import { createUseBankStatementsAvailabilityListKey } from 'services/bookkeeping/queries/useBankStatementsAvailabilityList';
import fetchBankStatementsAvailabilityList from 'services/bookkeeping/queries/useBankStatementsAvailabilityList/useBankStatementsAvailabilityList.request';
import type { BankStatementsAvailabilityListResponse } from 'services/bookkeeping/queries/useBankStatementsAvailabilityList/useBankStatementsAvailabilityList.interface';

// Waits before each re-read of a list that does not show the connection yet (the webhook that fills it can lag).
export const UNLISTED_CONNECTION_RETRY_DELAYS_MS = [1500, 3000, 5000];

/**
 * Which listed institution a new Quiltt connection belongs to.
 *
 * Quiltt's success callback names the connection but not the bank, and the member can clear the
 * prefilled search and connect a different one. The backend's availability list does pair each
 * account's connection with the account it covers, so that is the source of truth:
 *
 * - the connection covers an account in one of these institutions → that institution. Matched on the
 *   account id first, which also finds a bank listed under its name for want of an institution id,
 *   then on the Plaid institution id;
 * - it is listed, but against none of them → null: the member connected a bank that is not in this
 *   list, and crediting any row with it would record the wrong pairing;
 * - it is not listed → undefined. The list carries business accounts only, so a bank outside it never
 *   shows up, and that cannot be told apart from a webhook still on its way.
 */
export function resolveConnectedInstitutionId(
    availability: BankStatementsAvailabilityListResponse,
    connectionId: string,
    institutions: ConnectStatementsInstitution[]
): string | null | undefined {
    const connected = availability.filter((item) => item.connection_id === connectionId);
    if (connected.length === 0) {
        return undefined;
    }
    const accountIds = new Set(connected.map((item) => item.apiAccountId));
    const byAccount = institutions.find((inst) => inst.accounts.some((account) => accountIds.has(account.id)));
    if (byAccount) {
        return byAccount.institutionId;
    }
    const institutionIds = new Set(connected.map((item) => item.apiInstitutionId));
    return institutions.find((inst) => institutionIds.has(inst.institutionId))?.institutionId ?? null;
}

const wait = (ms: number) =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

/**
 * Reads the availability list — already refreshed by useQuilttConnect on success — to credit a connection,
 * re-reading it while the connection is missing. Never guesses: an unverified pairing would mark the wrong
 * bank synced and drop the right one from the sync list for good.
 *
 * Resolves to the institution id; null when the connection is to a bank outside the list; undefined when
 * it could not be confirmed (still unlisted after the retries, or the list failed to load).
 */
export default function useConnectedInstitutionResolver(institutions: ConnectStatementsInstitution[]) {
    const queryClient = useQueryClient();

    return useCallback(
        async (connectionId: string, prefilledInstitutionId: string): Promise<string | null | undefined> => {
            const queryKey = createUseBankStatementsAvailabilityListKey();
            const resolveAfter = async (retryDelays: number[]): Promise<string | null | undefined> => {
                const availability = await queryClient.fetchQuery({
                    queryKey,
                    queryFn: fetchBankStatementsAvailabilityList,
                    staleTime: 0,
                });
                const resolved = resolveConnectedInstitutionId(availability, connectionId, institutions);
                if (resolved !== undefined || retryDelays.length === 0) {
                    return resolved;
                }
                await wait(retryDelays[0]);
                return resolveAfter(retryDelays.slice(1));
            };

            let institutionId: string | null | undefined;
            try {
                const cached = await queryClient.ensureQueryData({
                    queryKey,
                    queryFn: fetchBankStatementsAvailabilityList,
                });
                institutionId = resolveConnectedInstitutionId(cached, connectionId, institutions);
                if (institutionId === undefined) {
                    await wait(UNLISTED_CONNECTION_RETRY_DELAYS_MS[0]);
                    institutionId = await resolveAfter(UNLISTED_CONNECTION_RETRY_DELAYS_MS.slice(1));
                }
            } catch {
                institutionId = undefined;
            }

            if (institutionId !== prefilledInstitutionId) {
                datadogRum.addAction('Auto Statements connection differs from prefilled institution', {
                    connectionId,
                    prefilledInstitutionId,
                    connectedInstitutionId: institutionId ?? null,
                    connectionListed: institutionId !== undefined,
                });
            }
            return institutionId;
        },
        [queryClient, institutions]
    );
}
